// src/firebase.js
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyBzbkMKEX7HX0zuiQtcNHzWcFuTOz8n6EM",
  authDomain: "inove-app-53fa7.firebaseapp.com",
  projectId: "inove-app-53fa7",
  storageBucket: "inove-app-53fa7.firebasestorage.app",
  messagingSenderId: "472703072034",
  appId: "1:472703072034:web:38b2c4698acf067ca3177b",
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// ─── AUTH ────────────────────────────────────────────────────

export async function loginUsuario(email, senha) {
  const cred = await signInWithEmailAndPassword(auth, email, senha);
  const perfil = await buscarPerfil(cred.user.uid);
  return { user: cred.user, perfil };
}

export async function logoutUsuario() {
  await signOut(auth);
}

export function observarAuth(callback) {
  return onAuthStateChanged(auth, callback);
}

export async function recuperarSenha(email) {
  await sendPasswordResetEmail(auth, email);
}

// ─── PERFIL ──────────────────────────────────────────────────

export async function buscarPerfil(uid) {
  // Tenta primeiro pelo ID do documento
  const snap = await getDoc(doc(db, "usuarios", uid));
  if (snap.exists()) return { ...snap.data(), uid };

  // Se não encontrar, busca pelo campo uid
  const q = query(collection(db, "usuarios"), where("uid", "==", uid));
  const snapQ = await getDocs(q);
  if (!snapQ.empty) return { ...snapQ.docs[0].data(), uid };

  return null;
}

// ─── CADASTRO DE CLIENTE ─────────────────────────────────────

export async function cadastrarCliente(dados) {
  const { nome, razaoSocial, email, senha, telefone, cnpj, endereco, vendedorId } = dados;
  const cred = await createUserWithEmailAndPassword(auth, email, senha);
  const uid = cred.user.uid;
  await setDoc(doc(db, "usuarios", uid), {
    uid,
    nome,
    razaoSocial: razaoSocial || "",
    email,
    telefone:   telefone || "",
    cnpj:       cnpj     || "",
    endereco:   endereco || {},
    perfil:     "cliente",
    vendedorId: vendedorId || null,
    criadoEm:   serverTimestamp(),
  });
  return { uid, nome, razaoSocial: razaoSocial || "", email, perfil: "cliente" };
}

// ─── CADASTRO DE VENDEDOR (pelo Admin) ───────────────────────

export async function cadastrarVendedor(dados) {
  const { nome, email, senha, telefone, whatsapp, comissao } = dados;
  const cred = await createUserWithEmailAndPassword(auth, email, senha);
  const uid = cred.user.uid;
  await setDoc(doc(db, "usuarios", uid), {
    uid, nome, email,
    telefone:          telefone || "",
    whatsapp:          whatsapp || telefone || "",
    comissao:          Number(comissao) || 5,
    perfil:            "vendedor",
    linkPersonalizado: uid.slice(0, 8),
    criadoEm:          serverTimestamp(),
  });
  return { uid, nome, email, perfil: "vendedor" };
}

// ─── ATUALIZAR VENDEDOR (pelo Admin) ─────────────────────────

export async function atualizarVendedor(id, dados) {
  await updateDoc(doc(db, "usuarios", id), {
    telefone:     dados.telefone || "",
    whatsapp:     dados.whatsapp || "",
    comissao:     Number(dados.comissao) || 0,
    atualizadoEm: serverTimestamp(),
  });
}

// ─── PEDIDOS ─────────────────────────────────────────────────

export async function salvarPedido(pedido) {
  const ref = await addDoc(collection(db, "pedidos"), {
    ...pedido,
    status:   "pendente",
    criadoEm: serverTimestamp(),
  });
  return ref.id;
}

export async function atualizarStatusPedido(pedidoId, novoStatus) {
  await updateDoc(doc(db, "pedidos", pedidoId), {
    status:       novoStatus,
    atualizadoEm: serverTimestamp(),
  });
}

export async function listarTodosPedidos() {
  const snap = await getDocs(collection(db, "pedidos"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listarPedidosCliente(clienteId) {
  const q = query(collection(db, "pedidos"), where("clienteId", "==", clienteId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listarPedidosVendedor(vendedorId) {
  const q = query(collection(db, "pedidos"), where("vendedorId", "==", vendedorId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listarVendedores() {
  const q = query(collection(db, "usuarios"), where("perfil", "==", "vendedor"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listarClientes() {
  const q = query(collection(db, "usuarios"), where("perfil", "==", "cliente"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── PRODUTOS ────────────────────────────────────────────────

export async function listarProdutos() {
  const snap = await getDocs(collection(db, "produtos"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listarProdutosPorEmpresa(empresa) {
  const q = query(
    collection(db, "produtos"),
    where("empresa", "==", empresa),
    where("ativo", "==", true)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function salvarProduto(dados) {
  const ref = await addDoc(collection(db, "produtos"), {
    ...dados,
    ativo:    true,
    criadoEm: serverTimestamp(),
  });
  return ref.id;
}

export async function atualizarProduto(produtoId, dados) {
  await updateDoc(doc(db, "produtos", produtoId), {
    ...dados,
    atualizadoEm: serverTimestamp(),
  });
}

export async function deletarProduto(produtoId) {
  await deleteDoc(doc(db, "produtos", produtoId));
}